import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { apiJson, apiUpload } from './api';
import { useSEO } from './useSEO';

/**
 * Admin Notes Builder.
 *
 * 1. Admin selects level / subject / board.
 * 2. The system loads (or auto-fetches) the official spec topic tree.
 * 3. Admin picks a job mode: a single topic, or the whole spec.
 * 4. Admin drops PDF / HTML source files.
 * 5. On submit, the backend creates a notes job and runs the
 *    summarisation pipeline asynchronously. We poll for status.
 * 6. When complete, results are listed by topic and can be inspected.
 *
 * Notes are stored per-topic in the database; the AI tutor agent
 * retrieves them on demand. Compiling a PDF is a separate later step.
 */
export default function AdminNotesBuilder() {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  useSEO({
    title: 'Admin: notes builder',
    description: 'Generate spec-faithful study notes and AI tutor context per topic.',
    canonicalPath: '/admin/notes',
  });

  useEffect(() => {
    if (user === null) navigate('/login');
    else if (user && !user.isAdmin) navigate('/');
  }, [user, navigate]);

  // ── Cascade selectors ─────────────────────────────────────────────────
  const [levels, setLevels] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [boards, setBoards] = useState([]);
  const [sel, setSel] = useState({ level: '', subject: '', board: '' });

  // ── Spec state ────────────────────────────────────────────────────────
  const [spec, setSpec] = useState(null);
  const [topics, setTopics] = useState([]);
  const [specBusy, setSpecBusy] = useState(false);
  const [specError, setSpecError] = useState('');

  // ── Job composition ───────────────────────────────────────────────────
  const [mode, setMode] = useState('topic'); // 'topic' | 'spec'
  const [topicId, setTopicId] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // ── Active job tracking ───────────────────────────────────────────────
  const [activeJob, setActiveJob] = useState(null);
  const pollRef = useRef(null);

  const dropRef = useRef(null);

  // ── Loaders ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.isAdmin) return;
    apiJson('/api/levels').then(setLevels).catch((e) => setError(e.message));
  }, [user]);

  useEffect(() => {
    if (!sel.level) return setSubjects([]);
    apiJson(`/api/subjects/${encodeURIComponent(sel.level)}`).then(setSubjects).catch(() => setSubjects([]));
  }, [sel.level]);

  useEffect(() => {
    if (!sel.level || !sel.subject) return setBoards([]);
    apiJson(`/api/boards/${encodeURIComponent(sel.level)}/${encodeURIComponent(sel.subject)}`)
      .then(setBoards).catch(() => setBoards([]));
  }, [sel.level, sel.subject]);

  const loadSpec = useCallback(async () => {
    if (!sel.level || !sel.subject || !sel.board) return;
    setSpecBusy(true); setSpecError('');
    try {
      const data = await apiJson(
        `/api/admin/notes/specs/${encodeURIComponent(sel.level)}/${encodeURIComponent(sel.subject)}/${encodeURIComponent(sel.board)}`,
        { token }
      );
      setSpec(data.spec);
      setTopics(data.topics || []);
    } catch (e) {
      setSpecError(e.message);
    } finally {
      setSpecBusy(false);
    }
  }, [sel.level, sel.subject, sel.board, user]);

  useEffect(() => {
    setSpec(null); setTopics([]); setTopicId('');
    if (sel.level && sel.subject && sel.board) loadSpec();
  }, [sel.level, sel.subject, sel.board, loadSpec]);

  const refreshSpec = async () => {
    setSpecBusy(true); setSpecError('');
    try {
      const data = await apiJson('/api/admin/notes/specs/refresh', {
        method: 'POST',
        token,
        body: { level: sel.level, subject: sel.subject, board: sel.board, force: true },
      });
      setSpec(data.spec);
      setTopics(data.topics || []);
    } catch (e) {
      setSpecError(e.message);
    } finally {
      setSpecBusy(false);
    }
  };

  // ── Drag/drop handlers ────────────────────────────────────────────────
  useEffect(() => {
    const node = dropRef.current;
    if (!node) return;
    const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
    const onDrop = (e) => {
      stop(e);
      const dropped = Array.from(e.dataTransfer.files || []);
      if (dropped.length) setFiles((prev) => [...prev, ...dropped]);
    };
    node.addEventListener('dragover', stop);
    node.addEventListener('drop', onDrop);
    return () => {
      node.removeEventListener('dragover', stop);
      node.removeEventListener('drop', onDrop);
    };
  }, []);

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  // ── Submit job ────────────────────────────────────────────────────────
  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!sel.level || !sel.subject || !sel.board) return setError('Pick level, subject and board.');
    if (mode === 'topic' && !topicId) return setError('Pick a topic for topic mode.');
    if (!files.length) return setError('Attach at least one source file.');

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('level', sel.level);
      fd.append('subject', sel.subject);
      fd.append('board', sel.board);
      fd.append('mode', mode);
      if (mode === 'topic') fd.append('topicId', topicId);
      files.forEach((f) => fd.append('sources', f));
      const res = await apiUpload('/api/admin/notes/jobs', fd, { token });
      setActiveJob({ id: res.jobId, status: res.status, progressPct: 0, progressMessage: 'Queued' });
      setFiles([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Poll active job ───────────────────────────────────────────────────
  useEffect(() => {
    if (!activeJob?.id) return;
    if (pollRef.current) clearInterval(pollRef.current);
    const poll = async () => {
      try {
        const data = await apiJson(`/api/admin/notes/jobs/${activeJob.id}`, { token });
        setActiveJob({ ...data.job, sources: data.sources, outputs: data.outputs });
        if (['completed', 'failed'].includes(data.job.status)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          // refresh topic-notes flags
          loadSpec();
        }
      } catch (err) {
        // keep polling
      }
    };
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); pollRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJob?.id]);

  // ── Render ────────────────────────────────────────────────────────────
  const topicTree = useMemo(() => buildTree(topics), [topics]);

  return (
    <div className="tt-page" style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1 style={{ marginBottom: 4 }}>Notes builder</h1>
        <Link to="/admin/notes/jobs" className="nav-link">All jobs →</Link>
      </div>
      <p style={{ color: 'var(--muted)', marginTop: 0 }}>
        Generate spec-faithful study notes and AI tutor context per topic. Notes live in the database;
        the AI tutor agent retrieves them on demand.
      </p>

      {/* Cascade selectors */}
      <div className="card" style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>1 · Subject</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <Select label="Level" value={sel.level} options={levels}
            onChange={(v) => setSel({ level: v, subject: '', board: '' })} />
          <Select label="Subject" value={sel.subject} options={subjects} disabled={!sel.level}
            onChange={(v) => setSel((p) => ({ ...p, subject: v, board: '' }))} />
          <Select label="Board" value={sel.board} options={boards} disabled={!sel.subject}
            onChange={(v) => setSel((p) => ({ ...p, board: v }))} />
        </div>
      </div>

      {/* Spec status */}
      {sel.level && sel.subject && sel.board && (
        <div className="card" style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>2 · Specification</h3>
            <button className="primary-btn" onClick={refreshSpec} disabled={specBusy}>
              {spec ? (specBusy ? 'Refreshing…' : 'Re-fetch spec') : (specBusy ? 'Fetching…' : 'Fetch spec')}
            </button>
          </div>
          {specError && <p style={errStyle}>{specError}</p>}
          {spec ? (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                Status: <strong>{spec.status}</strong>
                {spec.version && <> · Version: {spec.version}</>}
                {spec.fetchedAt && <> · Fetched {new Date(spec.fetchedAt).toLocaleString()}</>}
                {spec.sourceUrl && <> · <a href={spec.sourceUrl} target="_blank" rel="noreferrer">source</a></>}
              </div>
              <div style={{ marginTop: 10, maxHeight: 260, overflowY: 'auto', border: '1px solid var(--border, #ddd)', borderRadius: 6, padding: 10 }}>
                <TopicTree nodes={topicTree} />
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
                {topics.length} topics · {topics.filter((t) => t.hasStudentNotes).length} have notes
              </div>
            </div>
          ) : (
            <p style={{ color: 'var(--muted)', marginTop: 8 }}>
              No cached spec yet — click <strong>Fetch spec</strong> to find and parse the official document.
            </p>
          )}
        </div>
      )}

      {/* Job composition */}
      {spec?.status === 'ready' && (
        <form className="card" style={cardStyle} onSubmit={submit}>
          <h3 style={{ marginTop: 0 }}>3 · Build notes</h3>
          <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
            <label><input type="radio" checked={mode === 'topic'} onChange={() => setMode('topic')} /> Single topic</label>
            <label><input type="radio" checked={mode === 'spec'} onChange={() => setMode('spec')} /> Whole spec (all leaf topics)</label>
          </div>

          {mode === 'topic' && (
            <select value={topicId} onChange={(e) => setTopicId(e.target.value)}
                    style={{ width: '100%', padding: 8, marginBottom: 12 }}>
              <option value="">Select a topic…</option>
              {topics.map((t) => (
                <option key={t.id} value={t.id}>
                  {'  '.repeat(t.depth)}{t.code ? `${t.code} ` : ''}{t.title}
                  {t.hasStudentNotes ? '  ✓' : ''}
                </option>
              ))}
            </select>
          )}

          <div ref={dropRef} style={dropZoneStyle}>
            <p style={{ margin: '4px 0' }}>Drop PDF / HTML / TXT source files here, or</p>
            <input type="file" multiple accept=".pdf,.html,.htm,.txt,.md"
                   onChange={(e) => setFiles((prev) => [...prev, ...Array.from(e.target.files || [])])} />
            {files.length > 0 && (
              <ul style={{ textAlign: 'left', marginTop: 10 }}>
                {files.map((f, i) => (
                  <li key={i}>
                    {f.name} <small style={{ color: 'var(--muted)' }}>({Math.round(f.size / 1024)} KB)</small>
                    {' '}<button type="button" onClick={() => removeFile(i)}>×</button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {error && <p style={errStyle}>{error}</p>}
          <button type="submit" className="primary-btn" disabled={submitting} style={{ marginTop: 12 }}>
            {submitting ? 'Submitting…' : 'Generate notes'}
          </button>
        </form>
      )}

      {/* Active job status */}
      {activeJob && (
        <div className="card" style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Job {activeJob.id?.slice(0, 8)} · {activeJob.status}</h3>
          <div style={{ background: '#eee', borderRadius: 4, height: 8, overflow: 'hidden' }}>
            <div style={{
              width: `${activeJob.progressPct || 0}%`,
              height: '100%',
              background: activeJob.status === 'failed' ? '#c33' : '#3a7',
              transition: 'width 0.4s'
            }} />
          </div>
          <p style={{ fontSize: 13, color: 'var(--muted)' }}>{activeJob.progressMessage}</p>
          {activeJob.error && <p style={errStyle}>{activeJob.error}</p>}

          {Array.isArray(activeJob.outputs) && activeJob.outputs.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <strong>Outputs ({activeJob.outputs.length})</strong>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, marginTop: 6 }}>
                <thead>
                  <tr style={{ textAlign: 'left' }}>
                    <th>Code</th><th>Topic</th><th>Status</th><th>Missing</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {activeJob.outputs.map((o) => (
                    <tr key={o.id} style={{ borderTop: '1px solid var(--border, #eee)' }}>
                      <td>{o.code || '—'}</td>
                      <td>{o.title}</td>
                      <td>
                        <span style={{ color: o.status === 'completed' ? '#3a7' : o.status === 'failed' ? '#c33' : '#666' }}>
                          {o.status}
                        </span>
                      </td>
                      <td>{(o.missingStatements || []).length}</td>
                      <td>
                        {o.status === 'completed' && (
                          <Link to={`/admin/notes/jobs/${activeJob.id}/outputs/${o.id}`}>view</Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Select({ label, value, options, onChange, disabled }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>
      <span style={{ color: 'var(--muted)' }}>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} style={{ padding: 6 }}>
        <option value="">—</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function buildTree(flat) {
  const byId = new Map(flat.map((t) => [t.id, { ...t, children: [] }]));
  const roots = [];
  for (const t of flat) {
    const node = byId.get(t.id);
    if (t.parentId && byId.has(t.parentId)) byId.get(t.parentId).children.push(node);
    else roots.push(node);
  }
  return roots;
}

function TopicTree({ nodes }) {
  if (!nodes?.length) return null;
  return (
    <ul style={{ listStyle: 'none', paddingLeft: 14, margin: 0 }}>
      {nodes.map((n) => (
        <li key={n.id} style={{ padding: '2px 0' }}>
          <span style={{ color: n.hasStudentNotes ? '#3a7' : 'inherit' }}>
            {n.hasStudentNotes ? '✓ ' : '· '}
          </span>
          {n.code && <code style={{ background: '#f5f5f5', padding: '0 4px', borderRadius: 3, marginRight: 4 }}>{n.code}</code>}
          {n.title}
          {n.statementCount > 0 && <small style={{ color: 'var(--muted)' }}> ({n.statementCount} statements)</small>}
          {n.children?.length > 0 && <TopicTree nodes={n.children} />}
        </li>
      ))}
    </ul>
  );
}

const cardStyle = {
  background: 'var(--card-bg, #fff)',
  border: '1px solid var(--border, #ddd)',
  borderRadius: 8,
  padding: 16,
  marginTop: 16,
};
const dropZoneStyle = {
  border: '2px dashed var(--border, #bbb)',
  borderRadius: 8,
  padding: 18,
  textAlign: 'center',
  background: 'var(--card-bg, #fafafa)',
};
const errStyle = { color: '#c33', marginTop: 8 };
